import { css } from '@emotion/react';
import Star from 'practice_room/components/shared/stat-with-stars/star';
import { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import {
  halfSpacing,
  standardSpacing,
} from 'styles/global_defaults/scaffolding';
import { random } from 'underscore';
import { tooltipZIndex } from './constants';

type StarsConfettiProps = {
  children: React.ReactElement;
  showLeft?: boolean;
  showRight?: boolean;
};

type StarsElementProps = {
  show: boolean;
  className: string;
  refElement: MutableRefObject<HTMLDivElement>;
};

const containerStyles = css`
  width: 100%;
  position: relative;
`;

const styles = css`
  pointer-events: none;
  position: absolute;
  top: -${halfSpacing}px;
  width: ${standardSpacing}%;
  height: 100%;
  z-index: ${tooltipZIndex + 1};

  &.show-left {
    left: -${halfSpacing}px;
  }

  &.show-right {
    right: -${halfSpacing}px;
  }

  /* Styles associated with each star */
  .star {
    position: absolute;
    top: 0px;
    opacity: 0;
    font-size: 0px; /* To position svg correctly */

    svg {
      transform: translateZ(0px);
    }

    .close-star {
      transform: rotate(45deg);
    }
  }
`;

const STAR_MAX_SIZE = 10;

// Insert stars to the element given
const insertStars = (element) => {
  for (let i = 0; i < random(35, 45); i += 1) {
    // eslint-disable-next-line no-new
    new Star(element, STAR_MAX_SIZE);
  }
};

const StarsElement = ({ show, className, refElement }: StarsElementProps) => {
  if (show) {
    return (
      <div
        css={styles}
        ref={refElement}
        className={`star-item d-flex align-items-center text-success ${className}`}
      />
    );
  }
  return null;
};

const StarsConfetti = ({
  children,
  showLeft = false,
  showRight = false,
}: StarsConfettiProps) => {
  const leftElement = useRef(null);
  const rightElement = useRef(null);

  const showStars = useCallback((element) => {
    if (element?.offsetParent) {
      insertStars(element);
    }
  }, []);

  useEffect(() => {
    setTimeout(() => {
      if (showLeft) {
        showStars(leftElement.current);
      }
      if (showRight) {
        showStars(rightElement.current);
      }
    }, 100);
  }, [showLeft, showRight, showStars]);

  return (
    <div css={containerStyles}>
      <StarsElement show={showLeft} className='show-left' refElement={leftElement} />
      {children}
      <StarsElement show={showRight} className='show-right' refElement={rightElement} />
    </div>
  );
};

export default StarsConfetti;
